import { Controller, useWatch, useFormState } from 'react-hook-form';
import { Select as AntSelect, DatePicker, Input } from 'antd';
import { DurationUnitType } from '@src/services/plan/planTypes';
import { StyledError } from '@src/components/ui/Input/styles';
import { ETypographyIntents } from '@src/typings';
import { useTranslation } from 'react-i18next';
import { ICommonFormProps } from '@src/components/DynamicForm';
import { FormLabel } from '@src/components/ui';
import { INPUT_CONFIG } from '@src/constants/inputConfig';
import { sanitizeNumberInput } from '@src/utilities/input';

export function FormSelectPlanExpirationType(
    props: Readonly<ICommonFormProps>
) {
    const { t } = useTranslation();

    const { label, keyName, control, className, disabled, errors, formProps } =
        props;
    const hasError = Boolean(errors);
    const { touchedFields, isSubmitted } = useFormState({ control });

    const type = useWatch({
        control,
        name: `${keyName}.type`,
    });

    const planExpirationOptions: { label: string; value: string }[] = [
        { label: 'No expiration', value: 'NO_EXPIRATION' },
        { label: 'Fix date', value: 'FIXED' },
        { label: 'Duration', value: 'RELATIVE_FIRST_USE' },
    ];

    const durationUnitOptions: { label: string; value: DurationUnitType }[] = [
        { label: 'Day(s)', value: 'DAY' },
        { label: 'Week(s)', value: 'WEEK' },
        { label: 'Month(s)', value: 'MONTH' },
        { label: 'Year(s)', value: 'YEAR' },
    ];
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === '-') {
            e.preventDefault();
        }
    };

    return (
        <div className={className}>
            <Controller
                control={control}
                name={`${keyName}.type`}
                rules={{
                    required: {
                        value: true,
                        message: t('errors.formErrors.required', {
                            name: label,
                        }),
                    },
                    validate: (value, formValues) => {
                        const details = formValues?.[keyName];
                        if (!details) return true;

                        if (value === 'FIXED') {
                            if (!details.date) {
                                return t('errors.formErrors.required', {
                                    name: 'date',
                                });
                            }
                        }

                        if (value === 'RELATIVE_FIRST_USE') {
                            if (!details.duration) {
                                return t('errors.formErrors.required', {
                                    name: 'duration',
                                });
                            }
                            if (!details.unit) {
                                return t('errors.formErrors.required', {
                                    name: 'unit',
                                });
                            }
                        }

                        return true;
                    },
                }}
                render={({
                    field: { onChange, value },
                    fieldState: { error: fieldStateError },
                }) => {
                    const fieldName = `${keyName}.type`;
                    const error = fieldStateError;
                    const showError = Boolean(
                        error && (touchedFields[fieldName] || isSubmitted)
                    );
                    return (
                        <>
                            {label && <FormLabel label={label} required />}

                            <AntSelect
                                size="large"
                                status={hasError ? 'error' : undefined}
                                options={planExpirationOptions}
                                value={value}
                                onChange={onChange}
                                placeholder="Expiration Type"
                                showSearch
                                disabled={disabled || formProps?.typeDisabled}
                                style={{ width: '100%' }}
                            />
                            {showError && error?.message && (
                                <StyledError intent={ETypographyIntents.Badge}>
                                    {error.message}
                                </StyledError>
                            )}
                        </>
                    );
                }}
            />
            {type == 'RELATIVE_FIRST_USE' && (
                <div className="flex gap-4 mt-2">
                    <Controller
                        control={control}
                        name={`${keyName}.duration`}
                        rules={{
                            required: type === 'RELATIVE_FIRST_USE',
                        }}
                        render={({
                            field,
                            fieldState: { error: fieldStateError },
                        }) => {
                            const fieldName = `${keyName}.duration`;
                            const error = fieldStateError;
                            const showError = Boolean(
                                error &&
                                    (touchedFields[fieldName] || isSubmitted)
                            );
                            return (
                                <Input
                                    type="number"
                                    size="small"
                                    value={field.value}
                                    className="w-full"
                                    placeholder="Duration"
                                    disabled={disabled}
                                    status={showError ? 'error' : undefined}
                                    min={0}
                                    onChange={(e) => {
                                        field.onChange(
                                            sanitizeNumberInput(
                                                e.target.value,
                                                INPUT_CONFIG.DURATION.maxLength
                                            )
                                        );
                                    }}
                                    onKeyDown={handleKeyDown}
                                />
                            );
                        }}
                    />
                    <Controller
                        control={control}
                        name={`${keyName}.unit`}
                        rules={{
                            required: type === 'RELATIVE_FIRST_USE',
                        }}
                        render={({
                            field,
                            fieldState: { error: fieldStateError },
                        }) => {
                            const fieldName = `${keyName}.unit`;
                            const error = fieldStateError;
                            const showError = Boolean(
                                error &&
                                    (touchedFields[fieldName] || isSubmitted)
                            );
                            return (
                                <AntSelect
                                    size="large"
                                    options={durationUnitOptions}
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder="Unit"
                                    showSearch
                                    className="w-full"
                                    disabled={disabled}
                                    status={showError ? 'error' : undefined}
                                />
                            );
                        }}
                    />
                </div>
            )}
            {type == 'FIXED' && (
                <div className="mt-2">
                    <Controller
                        control={control}
                        name={`${keyName}.date`}
                        render={({
                            field,
                            fieldState: { error: fieldStateError },
                        }) => {
                            const fieldName = `${keyName}.date`;
                            const error = fieldStateError;
                            const showError = Boolean(
                                error &&
                                    (touchedFields[fieldName] || isSubmitted)
                            );
                            return (
                                <DatePicker
                                    size="large"
                                    className="w-full"
                                    value={field.value}
                                    onChange={field.onChange}
                                    status={showError ? 'error' : undefined}
                                />
                            );
                        }}
                    />
                </div>
            )}
        </div>
    );
}
